import {
  ArrowLeft,
  FileText,
  Play,
  ShieldCheck,
  UploadCloud,
} from "lucide-react";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useApi } from "../App.jsx";
import { FeedbackBanner, PageHeader, useRouter } from "@tribunalea/ui";

function mb(bytes) {
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}

export function NewRunPage() {
  const api = useApi();
  const { navigate } = useRouter();
  const [templates, setTemplates] = useState([]);
  const [limits, setLimits] = useState(null);
  const [templateId, setTemplateId] = useState("");
  const [files, setFiles] = useState([]);
  const [draggingPdfs, setDraggingPdfs] = useState(false);
  const [state, setState] = useState({ loading: true, submitting: false, error: "" });
  const pdfDragDepthRef = useRef(0);

  useEffect(() => {
    Promise.all([
      api.listTemplates(),
      api.limits(),
    ])
      .then(([items, configuredLimits]) => {
        setTemplates(items);
        setLimits(configuredLimits);
        setTemplateId(items[0]?.id || "");
        setState({ loading: false, submitting: false, error: "" });
      })
      .catch((error) => setState({
        loading: false,
        submitting: false,
        error: error.message,
      }));
  }, [api]);

  const totalBytes = useMemo(
    () => files.reduce((total, file) => total + file.size, 0),
    [files],
  );
  const valid = (
    templateId &&
    files.length >= 1 &&
    files.length <= (limits?.maxPdfFiles || 3) &&
    totalBytes <= (limits?.maxTotalBytes || 3 * 1024 * 1024)
  );

  const selectPdfFiles = (fileList) => {
    const incoming = [...(fileList || [])];
    if (!incoming.length) return;
    const pdfs = incoming.filter((file) =>
      file.type === "application/pdf"
      || file.name.toLocaleLowerCase("es").endsWith(".pdf"),
    );
    if (!pdfs.length) {
      setState((current) => ({
        ...current,
        error: "Solamente podés cargar archivos PDF.",
      }));
      return;
    }
    const maxFiles = limits?.maxPdfFiles || 3;
    const maxBytes = limits?.maxTotalBytes || 3 * 1024 * 1024;
    const selectedBytes = pdfs.reduce(
      (total, file) => total + file.size,
      0,
    );
    let error = "";
    if (pdfs.length > maxFiles) {
      error = `Podés procesar hasta ${maxFiles} PDFs por ejecución.`;
    } else if (selectedBytes > maxBytes) {
      error = `Los PDFs superan el límite total de ${mb(maxBytes)}.`;
    } else if (pdfs.length !== incoming.length) {
      error = "Se ignoraron archivos que no eran PDF.";
    }
    setFiles(pdfs);
    setState((current) => ({ ...current, error }));
  };

  const beginPdfDrag = (event) => {
    if (!Array.from(event.dataTransfer?.types || []).includes("Files")) {
      return;
    }
    event.preventDefault();
    pdfDragDepthRef.current += 1;
    setDraggingPdfs(true);
  };

  const continuePdfDrag = (event) => {
    if (!Array.from(event.dataTransfer?.types || []).includes("Files")) {
      return;
    }
    event.preventDefault();
    event.dataTransfer.dropEffect = "copy";
  };

  const endPdfDrag = (event) => {
    event.preventDefault();
    pdfDragDepthRef.current = Math.max(
      0,
      pdfDragDepthRef.current - 1,
    );
    if (pdfDragDepthRef.current === 0) setDraggingPdfs(false);
  };

  const dropPdfs = (event) => {
    event.preventDefault();
    pdfDragDepthRef.current = 0;
    setDraggingPdfs(false);
    selectPdfFiles(event.dataTransfer?.files);
  };

  const submit = async () => {
    if (!valid || state.submitting) return;
    setState((current) => ({ ...current, submitting: true, error: "" }));
    try {
      const body = new FormData();
      body.append("templateId", templateId);
      files.forEach((file) => body.append("documents", file));
      const run = await api.createRun(body);
      navigate(`/procesador-documental/procesamientos/${run.id}`);
    } catch (error) {
      setState((current) => ({
        ...current,
        submitting: false,
        error: error.message,
      }));
    }
  };

  return (
    <div className="page narrow-page">
      <button
        className="back-link"
        onClick={() => navigate("/procesador-documental")}
      >
        <ArrowLeft size={16} /> Volver
      </button>
      <PageHeader
        eyebrow="Nuevo procesamiento"
        title="Procesá documentos del mismo asunto"
        description="Instrumenta combinará la información, señalará sus fuentes y esperará tu revisión antes de generar el resultado."
      />

      {state.error && <FeedbackBanner>{state.error}</FeedbackBanner>}

      <div className="run-builder">
        <section className="form-card">
          <div className="form-card-title">
            <span className="step-number">1</span>
            <div>
              <h2>Elegí una plantilla</h2>
              <p>Se utilizará la versión publicada actualmente.</p>
            </div>
          </div>
          {templates.length ? (
            <select
              data-justiniano-target="run-template"
              className="large-select"
              value={templateId}
              onChange={(event) => setTemplateId(event.target.value)}
            >
              {templates.map((template) => (
                <option value={template.id} key={template.id}>
                  {template.name} · versión {template.currentVersion}
                </option>
              ))}
            </select>
          ) : !state.loading && (
            <div className="inline-empty">
              No tenés plantillas activas.
              <button
                onClick={() =>
                  navigate("/procesador-documental/plantillas/nueva")
                }
              >
                Crear una
              </button>
            </div>
          )}
        </section>

        <section className="form-card">
          <div className="form-card-title">
            <span className="step-number">2</span>
            <div>
              <h2>Subí los PDFs</h2>
              <p>Todos deben corresponder al mismo expediente o asunto.</p>
            </div>
          </div>
          <label
            data-justiniano-target="run-files"
            tabIndex={-1}
            className={`file-drop ${draggingPdfs ? "is-dragging" : ""}`}
            onDragEnter={beginPdfDrag}
            onDragOver={continuePdfDrag}
            onDragLeave={endPdfDrag}
            onDrop={dropPdfs}
          >
            <UploadCloud size={34} />
            <span>
              <strong>
                {draggingPdfs
                  ? "Soltá los PDFs acá"
                  : `Seleccionar o arrastrar hasta ${limits?.maxPdfFiles || 3} PDFs`}
              </strong>
              <small>
                {limits?.maxPages || 60} páginas y{" "}
                {mb(limits?.maxTotalBytes || 3 * 1024 * 1024)} en total
              </small>
            </span>
            <input
              type="file"
              accept="application/pdf,.pdf"
              multiple
              onChange={(event) => {
                selectPdfFiles(event.target.files);
                event.target.value = "";
              }}
            />
          </label>
          {files.length > 0 && (
            <div className="selected-files">
              {files.map((file) => (
                <div key={`${file.name}-${file.size}`}>
                  <FileText size={18} />
                  <span><strong>{file.name}</strong><small>{mb(file.size)}</small></span>
                </div>
              ))}
              <div className={`file-total ${!valid ? "invalid" : ""}`}>
                {files.length}/{limits?.maxPdfFiles || 3} archivos ·{" "}
                {mb(totalBytes)}/{mb(limits?.maxTotalBytes || 3 * 1024 * 1024)}
              </div>
            </div>
          )}
        </section>

        <div className="process-note">
          <ShieldCheck size={21} />
          <span>
            <strong>Siempre vas a revisar el resultado.</strong>
            Instrumenta no modificará la plantilla original.
          </span>
        </div>

        <button
          data-justiniano-target="run-start"
          className="primary-button large-button"
          disabled={!valid || state.submitting}
          onClick={submit}
        >
          <Play size={18} />
          {state.submitting ? "Preparando documentos…" : "Comenzar procesamiento"}
        </button>
      </div>
    </div>
  );
}
